/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { SpecificFieldUpdate, useConnectorMutations, useConnectorQuery } from '../services/connector';
import Badge from '../components/ui/Badge/Badge';
import { resolveBadgeTypeForStatus } from '../utils/general';
import TabBar from '../components/layouts/TabBar/TabBar';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import { OverlayActions } from '../providers/OverlayProvider';
import { IntegrationImportForm } from '../components/layouts/IntegrationOverlay/IntegrationImportForm';
import { IntegrationSetupForm } from '../components/layouts/IntegrationOverlay/IntegrationSetupForm';
import { useNavigationItems } from '../hooks/useNavigationItems';

type Props = {
  editMode?: boolean;
  onClose: () => void;
  activateId?: number;
};
const IntegrationOverlay: React.FC<Props> = ({ editMode, onClose, activateId }) => {
  const { id } = useParams();
  const numericId = activateId || Number(id);

  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(0);
  const importFormRef = useRef<HTMLFormElement>(null);
  const setupFormRef = useRef<HTMLFormElement>(null);
  const { data: connector } = useConnectorQuery(numericId);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('integrations');

  const tabs = [
    {
      label: t('navbar.settings'),
      onClick: () => setActiveTab(0),
      visible: () => true,
    },
    {
      label: t('integrations.credentials'),
      onClick: () => setActiveTab(1),
      visible: () => true,
    },
  ];

  const { updateMutation, createMutation } = useConnectorMutations();

  const formatSetupFormData = (obj: SpecificFieldUpdate) => {
    return Object.entries(obj).map(([key, value]) => {
      if (typeof value === 'object' && value !== null && 'name' in value && 'value' in value) {
        return { name: key, value };
      }

      if (typeof value === 'string') {
        return { name: key, value: value.split(' ')[0] };
      }

      return { name: key, value };
    });
  };

  const onSubmitImport = async () => {
    if (editMode) {
      await updateMutation.mutateAsync(
        {
          params: {
            path: {
              connector_id: numericId,
            },
          },
          body: {
            specific_fields: connector?.template?.specific_fields,
            active: true,
          },
        },
        {
          onSuccess: () => {
            toast.success(t('success.integration_updated'));
          },
        },
      );
    } else {
      await createMutation.mutateAsync(
        {
          body: {
            specific_fields: connector?.template?.specific_fields,
            connector_tmpl_id: Number(connector?.template?.id),
            active: true,
          },
        },
        {
          onSuccess: () => {
            toast.success(t('success.integration_created'));
          },
        },
      );
    }
  };

  const onSubmitSetup = async (data: any) => {
    try {
      if (editMode) {
        await updateMutation.mutateAsync(
          {
            params: {
              path: {
                connector_id: numericId,
              },
            },
            body: {
              specific_fields: formatSetupFormData(data),
            },
          },
          {
            onSuccess: () => {
              toast.success(t('success.integration_updated'));
            },
          },
        );
      } else {
        await createMutation.mutateAsync(
          {
            body: {
              specific_fields: formatSetupFormData(data),
              connector_tmpl_id: Number(connector?.template?.id),
              active: true,
            },
          },
          {
            onSuccess: () => {
              toast.success(t('success.integration_created'));
            },
          },
        );
      }
    } catch (error) {
      toast.error(t('errors.integration_update_failed'));
    }
  };

  const triggerSubmit = () => {
    if (!editMode && setupFormRef.current) {
      setupFormRef.current.requestSubmit();
    } else {
      if (activeTab === 0 && importFormRef.current) {
        importFormRef.current.requestSubmit();
      }
      if (activeTab === 1 && setupFormRef.current) {
        setupFormRef.current.requestSubmit();
      }
    }
  };

  return (
    <ContainerBody className="h-full">
      <OverlayActions>
        <Button title={t('general.cancel')} onClick={() => onClose()} tone={ButtonToneProps.SECONDARY} />
        {activeTab === 0 && (
          <Button
            title={t('general.save')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            onClick={() => {
              if (importFormRef.current) {
                importFormRef.current.save();
              }
            }}
          />
        )}
        {(activeTab === 1 || !editMode) && (
          <Button
            type="submit"
            title={editMode ? t('general.save') : t('integrations.add_and_activate')}
            tone={ButtonToneProps.PRIMARY_BLUE}
            onClick={triggerSubmit}
          />
        )}
      </OverlayActions>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-start w-full gap-[12px]">
          <div className="flex items-center gap-10 flex-1 min-w-0">
            <h1 className="typo-title-24 md:typo-title-36 !leading-[120%] max-w-[230px] md:max-w-none truncate overflow-hidden text-ellipsis whitespace-nowrap">
              {connector?.name}
            </h1>
            {connector?.active && (
              <Badge label="Active" expanded showDot dotFilled type={resolveBadgeTypeForStatus('successful')} />
            )}
          </div>
          <p className={`typo-text-12 text-faded ${!editMode && 'mb-[40px]'}`}>
            {connector?.template?.channel?.description}
          </p>
        </div>
        {editMode ? (
          <>
            <TabBar className="my-[24px] md:my-[40px]" tabs={tabs} defaultIndex={0} />
            {activeTab === 0 ? (
              <IntegrationImportForm
                ref={importFormRef}
                connector={connector}
                allowEdition={accessRights?.read_write}
              />
            ) : (
              <IntegrationSetupForm
                ref={setupFormRef}
                onSubmit={onSubmitSetup}
                connector={connector}
                allowEdition={accessRights?.read_write}
              />
            )}
          </>
        ) : (
          <IntegrationSetupForm
            ref={setupFormRef}
            onSubmit={onSubmitSetup}
            connector={connector}
            allowEdition={accessRights?.read_write}
          />
        )}
      </div>
    </ContainerBody>
  );
};

export default IntegrationOverlay;
